// src/components/LearningEcosystem.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./LearningEcosystem.css";

const LearningEcosystem = () => {
  const [activePhase, setActivePhase] = useState(2);
  const [isVisible, setIsVisible] = useState(false);
  const [theme, setTheme] = useState("light");
  const [reduceMotion, setReduceMotion] = useState(false);

  const sectionRef = useRef(null);

  const [tip, setTip] = useState({
    show: false,
    x: 0,
    y: 0,
    month: "",
    title: "",
    bullets: [],
  });

  const [hoverIdx, setHoverIdx] = useState(null);
  const [marker, setMarker] = useState({ x: 0, y: 0 });

  // Sync with global theme (data-theme on <html>)
  useEffect(() => {
    const updateTheme = () => {
      const globalTheme =
        document.documentElement.getAttribute("data-theme") || "light";
      setTheme(globalTheme);
    };
    updateTheme();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === "attributes" && m.attributeName === "data-theme") {
          updateTheme();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(!!mq?.matches);
    apply();
    mq?.addEventListener?.("change", apply);
    return () => mq?.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.12 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  // ✅ THEME PALETTES
  const palette = useMemo(() => {
    if (theme === "dark") {
      return {
        p1: { c: "#ff9800", g: "linear-gradient(135deg, #ff9800 0%, #ffc107 55%, #ff5722 100%)" },
        p2: { c: "#ffc107", g: "linear-gradient(135deg, #ffc107 0%, #ffd54f 70%, #ff9800 100%)" },
        p3: { c: "#ff5722", g: "linear-gradient(135deg, #ff5722 0%, #ff9800 55%, #ffc107 100%)" },
        p4: { c: "#ffd54f", g: "linear-gradient(135deg, #ffd54f 0%, #ffc107 60%, #ff9800 100%)" },
      };
    }
    return {
      p1: { c: "#2563eb", g: "linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)" },
      p2: { c: "#0ea5e9", g: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)" },
      p3: { c: "#6366f1", g: "linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)" },
      p4: { c: "#10b981", g: "linear-gradient(135deg, #10b981 0%, #34d399 100%)" },
    };
  }, [theme]);

  const phases = useMemo(
    () => [
      {
        id: 1,
        month: "MONTH 1",
        title: "Building Foundation",
        icon: "⬡",
        color: palette.p1.c,
        gradient: palette.p1.g,
        skills: ["Python Mastery", "DSA Fundamentals", "Statistics & Math", "ML Basics"],
        metrics: { projects: "3 Projects", hours: "120+ Hours" },
        description: "Strong basics, problem-solving and mentor-led guidance",
      },
      {
        id: 2,
        month: "MONTH 2",
        title: "Internship Enrollment",
        icon: "⌁",
        color: palette.p2.c,
        gradient: palette.p2.g,
        skills: ["Partner Projects", "Mentor Support", "Git & Collaboration", "Agile Workflow"],
        metrics: { projects: "2 Projects", hours: "160+ Hours" },
        description: "Work with real teams, real tasks and proper dev workflow",
      },
      {
        id: 3,
        month: "MONTH 3–5",
        title: "Collaborative Product Development",
        icon: "⚑",
        color: palette.p3.c,
        gradient: palette.p3.g,
        skills: ["Full-Stack Dev", "ML Model Building", "Code Reviews", "System Design"],
        metrics: { projects: "5+ Projects", hours: "480+ Hours" },
        description: "Build features, ship releases and create strong portfolio proof",
      },
      {
        id: 4,
        month: "MONTH 6",
        title: "Testing & Deployment",
        icon: "⤴",
        color: palette.p4.c,
        gradient: palette.p4.g,
        skills: ["QA & Testing", "Cloud Deployment", "Portfolio", "Placement Prep"],
        metrics: { projects: "8+ Projects", hours: "600+ Hours" },
        description: "Deploy, polish portfolio and get interview-ready",
      },
    ],
    [palette]
  );

  const topCards = useMemo(
    () => [
      { title: "Monthly Stipend", value: "Upto 10K", icon: "💰" },
      { title: "Mock Interviews", value: "4+/Week", icon: "🎤" },
      { title: "Tech Discussions", value: "Daily", icon: "💬" },
      { title: "Partner Companies", value: "10+", icon: "🏢" },
    ],
    []
  );

  const graphPoints = useMemo(
    () => [
      { m: 0, v: 12, short: "01" },
      { m: 1, v: 34, short: "05" },
      { m: 2, v: 56, short: "10" },
      { m: 3, v: 70, short: "15" },
      { m: 4, v: 80, short: "20" },
      { m: 5, v: 90, short: "30" },
      { m: 6, v: 96, short: "40" },
    ],
    []
  );

  const pointInfo = useMemo(
    () => ({
      0: { month: "Start", title: "Kickoff & Roadmap", bullets: ["Orientation + goal setting", "Tools setup (Git, workflow)", "Your plan starts here"] },
      1: { month: "Month 1", title: "Foundation", bullets: ["Python + DSA + ML basics", "Weekly assessments", "Mentor-led problem solving"] },
      2: { month: "Month 2", title: "Internship Enrollment", bullets: ["Partner tasks", "Git + reviews", "Team collaboration"] },
      3: { month: "Month 3", title: "Product Building", bullets: ["Build real features", "Full-stack/ML modules", "Daily discussions"] },
      4: { month: "Month 4", title: "Level Up", bullets: ["Advanced concepts", "Architecture + optimization", "Mock interviews"] },
      5: { month: "Month 5", title: "Portfolio Polish", bullets: ["Ship complete features", "Refinement + reviews", "Interview practice"] },
      6: { month: "Month 6", title: "Testing & Deployment", bullets: ["Testing + cloud deploy", "Portfolio polished", "Placement preparation"] },
    }),
    []
  );

  const active = phases.find((p) => p.id === activePhase) || phases[0];

  const svg = useMemo(() => {
    const W = 1200;
    const H = 420;
    const padX = 68;
    const padTop = 52;
    const padBottom = 76;

    const x = (m) => padX + (m / 6) * (W - padX * 2);
    const y = (v) => (H - padBottom) - (v / 100) * ((H - padBottom) - padTop);

    const pts = graphPoints.map((p) => ({ ...p, x: x(p.m), y: y(p.v) }));

    const smooth = (points) => {
      if (points.length < 2) return "";
      let path = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const cx = (p0.x + p1.x) / 2;
        path += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
      }
      return path;
    };

    const dMain = smooth(pts);
    const pts2 = pts.map((p, i) => ({ ...p, y: p.y + (i % 2 === 0 ? 16 : 10) }));
    const dAlt = smooth(pts2);

    const baseline = H - padBottom;
    const area = `${dMain} L ${pts[pts.length - 1].x} ${baseline} L ${pts[0].x} ${baseline} Z`;

    return { W, H, pts, dMain, dAlt, padX, padTop, padBottom, baseline, area };
  }, [graphPoints]);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const nearestPointFromEvent = (e) => {
    const svgEl = e.currentTarget;
    const rect = svgEl.getBoundingClientRect();
    const mx = e.clientX - rect.left;

    let nearest = svg.pts[0];
    let best = Infinity;
    let idx = 0;

    svg.pts.forEach((p, i) => {
      const px = (p.x / svg.W) * rect.width;
      const dist = Math.abs(px - mx);
      if (dist < best) {
        best = dist;
        nearest = p;
        idx = i;
      }
    });

    return { nearest, rect, idx };
  };

  const showTipForPoint = (nearest, rect, idx) => {
    const info = pointInfo[nearest.m] || { month: "Month", title: "Update", bullets: [] };

    const px = (nearest.x / svg.W) * rect.width;
    const py = (nearest.y / svg.H) * rect.height;

    const safeX = clamp(px, 210, rect.width - 210);
    const safeY = clamp(py, 85, rect.height - 160);

    setTip({
      show: true,
      x: safeX,
      y: safeY,
      month: info.month,
      title: info.title,
      bullets: info.bullets,
    });

    setHoverIdx(idx);
    setMarker({ x: nearest.x, y: nearest.y });

    if (nearest.m <= 1) setActivePhase(1);
    else if (nearest.m === 2) setActivePhase(2);
    else if (nearest.m >= 3 && nearest.m <= 5) setActivePhase(3);
    else setActivePhase(4);
  };

  const onGraphMove = (e) => {
    const { nearest, rect, idx } = nearestPointFromEvent(e);
    showTipForPoint(nearest, rect, idx);
  };

  const onGraphLeave = () => {
    setTip((t) => ({ ...t, show: false }));
    setHoverIdx(null);
  };

  const onTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    const fakeEvent = { currentTarget: e.currentTarget, clientX: t.clientX };
    const { nearest, rect, idx } = nearestPointFromEvent(fakeEvent);
    showTipForPoint(nearest, rect, idx);
  };

  // Donut palette stays as your logic
  const donut = useMemo(() => {
    const total = 6;

    const palette =
      theme === "dark"
        ? { foundation: "#ff9800", internship: "#ffc107", product: "#ff5722", deploy: "#ffd54f" }
        : { foundation: "#2563eb", internship: "#0ea5e9", product: "#6366f1", deploy: "#10b981" };

    const items = [
      { label: "Foundation", value: 1, color: palette.foundation },
      { label: "Internship", value: 1, color: palette.internship },
      { label: "Product Dev", value: 3, color: palette.product },
      { label: "Deploy", value: 1, color: palette.deploy },
    ];

    const r = 64;
    const c = 2 * Math.PI * r;
    let acc = 0;

    const segs = items.map((it) => {
      const frac = it.value / total;
      const len = c * frac;
      const start = (acc / total) * c;
      acc += it.value;
      return { ...it, c, r, len, start };
    });

    return { items, segs, r, c };
  }, [theme]);

  return (
    <section
      className={`ecoPro ${isVisible ? "ecoPro--in" : ""} ${reduceMotion ? "ecoPro--rm" : ""}`}
      ref={sectionRef}
      id="ecosystem"
      data-theme={theme}
    >
      <div className="ecoPro__aurora" aria-hidden="true" />
      <div className="ecoPro__grid" aria-hidden="true" />

      <div className="ecoPro__wrap">
        <header className="ecoPro__head">
          <h2 className="ecoPro__title">
            From Beginner to <span className="ecoPro__grad">Industry-Ready Developer</span>
          </h2>

          <p className="ecoPro__sub">
            Learn with mentors, <strong>ship real projects</strong>, work in team workflows,
            and graduate with a strong portfolio + interview prep.
          </p>
        </header>

        <div className="ecoPro__cards">
          {topCards.map((c, i) => (
            <div key={i} className="ecoTopCard">
              <div className="ecoTopCard__icon">{c.icon}</div>
              <div className="ecoTopCard__meta">
                <div className="ecoTopCard__title">{c.title}</div>
                <div className="ecoTopCard__value">{c.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="ecoPro__dash">
          <aside className="ecoPanePro ecoPanePro--left">
            <div className="ecoPanePro__miniHead">
              <div className="ecoPanePro__miniTitle">Programme Split</div>
              <div className="ecoPanePro__miniSub">How 6 months is structured</div>
            </div>

            <div className="ecoDonutPro">
              <svg viewBox="0 0 180 180" className="ecoDonutPro__svg" aria-hidden="true">
                <circle cx="90" cy="90" r={donut.r} className="ecoDonutPro__track" />
                {donut.segs.map((s, idx) => (
                  <circle
                    key={idx}
                    cx="90"
                    cy="90"
                    r={s.r}
                    className="ecoDonutPro__seg"
                    style={{
                      stroke: s.color,
                      strokeDasharray: `${s.len} ${s.c - s.len}`,
                      strokeDashoffset: `${-s.start}`,
                    }}
                  />
                ))}
                <circle cx="90" cy="90" r="44" className="ecoDonutPro__hole" />
                <text x="90" y="92" textAnchor="middle" className="ecoDonutPro__center">
                  6 mo
                </text>
              </svg>

              <div className="ecoLegendPro">
                {donut.items.map((it, i) => (
                  <div key={i} className="ecoLegendPro__row">
                    <span className="ecoLegendPro__dot" style={{ background: it.color }} />
                    <div className="ecoLegendPro__txt">
                      <div className="ecoLegendPro__name">{it.label}</div>
                      <div className="ecoLegendPro__meta">
                        {it.value} month{it.value > 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="ecoLegendPro__pill">{Math.round((it.value / 6) * 100)}%</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ecoBadgesPro">
              <span className="ecoBadgePro">✅ Real projects</span>
              <span className="ecoBadgePro">☁️ Cloud deployment</span>
              <span className="ecoBadgePro">🧠 Mentor reviews</span>
            </div>
          </aside>

          <main className="ecoPanePro ecoPanePro--right">
            <div className="ecoChartProWrap">
              <svg
                viewBox={`0 0 ${svg.W} ${svg.H}`}
                className="ecoChartPro"
                aria-hidden="true"
                onMouseMove={onGraphMove}
                onMouseEnter={onGraphMove}
                onMouseLeave={onGraphLeave}
                onTouchStart={onTouchMove}
                onTouchMove={onTouchMove}
              >
                <defs>
                  <linearGradient id="ecoLineAPro" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="var(--chartA1)" />
                    <stop offset="55%" stopColor="var(--chartA2)" />
                    <stop offset="100%" stopColor="var(--chartA3)" />
                  </linearGradient>

                  <linearGradient id="ecoLineBPro" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="var(--chartB1)" />
                    <stop offset="100%" stopColor="var(--chartB2)" />
                  </linearGradient>

                  <linearGradient id="ecoFillPro" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chartFillTop)" stopOpacity="1" />
                    <stop offset="65%" stopColor="var(--chartFillMid)" stopOpacity="1" />
                    <stop offset="100%" stopColor="var(--chartFillBot)" stopOpacity="1" />
                  </linearGradient>
                </defs>

                {[20, 40, 60, 80, 100].map((v) => {
                  const y = svg.padTop + (1 - v / 100) * (svg.H - svg.padTop - svg.padBottom);
                  return (
                    <line
                      key={v}
                      x1={svg.padX}
                      x2={svg.W - svg.padX}
                      y1={y}
                      y2={y}
                      className="ecoChartPro__grid"
                    />
                  );
                })}

                <line
                  x1={svg.padX}
                  x2={svg.W - svg.padX}
                  y1={svg.baseline}
                  y2={svg.baseline}
                  className="ecoChartPro__base"
                />

                <path d={svg.area} fill="url(#ecoFillPro)" className="ecoChartPro__fill" />
                <path d={svg.dAlt} className="ecoChartPro__line2" stroke="url(#ecoLineBPro)" />
                <path d={svg.dMain} className="ecoChartPro__line" stroke="url(#ecoLineAPro)" />

                {svg.pts.map((p, i) => (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="5" className="ecoChartPro__pt" />
                    <circle cx={p.x} cy={p.y} r="18" className="ecoChartPro__hit" />
                  </g>
                ))}

                {svg.pts.map((p, i) => (
                  <text
                    key={i}
                    x={p.x}
                    y={svg.H - 18}
                    textAnchor="middle"
                    className="ecoChartPro__x"
                  >
                    {p.short}
                  </text>
                ))}

                {hoverIdx !== null && (
                  <g className="ecoChartPro__hover">
                    <line
                      x1={svg.pts[hoverIdx].x}
                      x2={svg.pts[hoverIdx].x}
                      y1={svg.padTop}
                      y2={svg.baseline}
                      className="ecoChartPro__vline"
                    />
                    <circle cx={marker.x} cy={marker.y} r="10" className="ecoChartPro__marker" />
                    <circle cx={marker.x} cy={marker.y} r="22" className="ecoChartPro__ring" />
                  </g>
                )}
              </svg>

              {tip.show && (
                <div
                  className="ecoHoverPro"
                  style={{ "--phaseGrad": active.gradient, left: tip.x, top: tip.y }}
                >
                  <div className="ecoHoverPro__icon">{active.icon}</div>
                  <div className="ecoHoverPro__body">
                    <div className="ecoHoverPro__k">{tip.month}</div>
                    <div className="ecoHoverPro__t">{tip.title}</div>
                    <ul className="ecoHoverPro__list">
                      {tip.bullets.slice(0, 3).map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        <div className="ecoPhaseWrap">
          <div className="ecoPhaseInner">
            {phases.map((p) => (
              <div
                key={p.id}
                className={`ecoPhaseCard ${activePhase === p.id ? "ecoPhaseCard--active" : ""}`}
                style={{ "--pg": p.gradient, "--pc": p.color }}
                onMouseEnter={() => setActivePhase(p.id)}
                onFocus={() => setActivePhase(p.id)}
                tabIndex={0}
              >
                <div className="ecoPhaseTop">
                  <div className="ecoPhaseBadge">{p.month}</div>
                  <div className="ecoPhaseIcon">{p.icon}</div>
                </div>

                <h3 className="ecoPhaseTitle">{p.title}</h3>
                <p className="ecoPhaseDesc">{p.description}</p>

                <div className="ecoPhaseChips">
                  <span className="ecoChip">🎯 {p.metrics.projects}</span>
                  <span className="ecoChip">⏱️ {p.metrics.hours}</span>
                </div>

                <div className="ecoPhasePopover">
                  <div className="ecoPhasePopover__head">What you’ll achieve</div>
                  <ul className="ecoPhasePopover__list">
                    {p.skills.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearningEcosystem;